/** Live snapshots carry competition values; the administrator catalog owns definitions. */
export function hydrateCustomDisciplineGroupSchema<
  T extends {
    customDisciplineGroup?: {
      groupTypeId: string;
      groupType?: unknown;
    } | null;
  },
  S extends { id: string },
>(group: T, schemas: readonly S[]): T {
  const custom = group.customDisciplineGroup;
  if (!custom) return group;
  const schema = schemas.find((item) => item.id === custom.groupTypeId);
  if (!schema || schema === custom.groupType) return group;
  return {
    ...group,
    customDisciplineGroup: { ...custom, groupType: schema },
  };
}

/** Behavior caches must not serialize the schema and nested athletes per cell. */
export function customDisciplineRuntimeMetadata<T>(group: T) {
  const {
    customDisciplineGroup: _schema,
    athletes: _athletes,
    ...metadata
  } = group as T & { customDisciplineGroup?: unknown; athletes?: unknown };
  return metadata;
}

/** Reuse immutable, flattened Behavior records when an IPC snapshot changes
 * only the nested roster. New/removed keys and reference changes still publish. */
export function shareCustomDisciplineRuntimeRecord<T extends object>(
  previous: T | undefined,
  incoming: T,
): T {
  if (!previous) return incoming;
  const keys = Object.keys(incoming) as (keyof T)[];
  return keys.length === Object.keys(previous).length &&
    keys.every((key) => Object.is(previous[key], incoming[key]))
    ? previous
    : incoming;
}

export function shareCustomDisciplineRuntimeRecords<T extends { id?: unknown }>(
  previous: readonly T[],
  incoming: T[],
): T[] {
  const byId = new Map(previous.map((record) => [record.id, record]));
  const next = incoming.map((record) =>
    shareCustomDisciplineRuntimeRecord(byId.get(record.id), record),
  );
  return next.length === previous.length &&
    next.every((record, index) => record === previous[index])
    ? (previous as T[])
    : next;
}

/** IPC returns fresh objects for every row. Preserve unchanged EAV snapshots so
 * grid memoization and undo history retain only athletes that actually changed. */
export function shareCustomDisciplineAthletes<
  T extends {
    id: string;
    groupId: string;
    order: number;
    values: readonly {
      id: string;
      fieldDefinitionId: string;
      textValue?: unknown;
      numberValue?: unknown;
      selectOptionId?: unknown;
    }[];
  },
>(previous: readonly T[], incoming: T[]): T[] {
  const byId = new Map(previous.map((athlete) => [athlete.id, athlete]));
  const next = incoming.map((athlete) => {
    const old = byId.get(athlete.id);
    if (
      !old ||
      old.groupId !== athlete.groupId ||
      old.order !== athlete.order ||
      old.values.length !== athlete.values.length
    )
      return athlete;
    const values = new Map(old.values.map((value) => [value.id, value]));
    const unchanged = athlete.values.every((value) => {
      const previous = values.get(value.id);
      return (
        previous &&
        previous.fieldDefinitionId === value.fieldDefinitionId &&
        Object.is(previous.textValue, value.textValue) &&
        Object.is(previous.numberValue, value.numberValue) &&
        Object.is(previous.selectOptionId, value.selectOptionId)
      );
    });
    return unchanged ? old : athlete;
  });
  return next.length === previous.length &&
    next.every((athlete, index) => athlete === previous[index])
    ? (previous as T[])
    : next;
}
